import { z } from 'zod'

export const Sport = z.object({
  id: z.number(),
  name: z.string(),
  color: z.string()
})

export const Property = z.object({
  id: z.number(),
  name: z.string(),
  multiCDN: z.boolean(),
  logoUrl: z.string().nullable(),
  drm: z.boolean(),
  awsProvisioning: z.string(),
  lowLatency: z.string(),
  lowLatencyHost: z.number().nullable(),
  autoProvisioning: z.string(),
  autoProvisioningOffsetMins: z.number().nullable(),
  llHls: z.string(),
  normalizeInputStream: z.string(),
  useSSAI: z.string(),
  license: z.null(),
  sport: Sport
})

export const ZixiServer = z.object({
  id: z.number(),
  ip: z.string(),
  hostname: z.string().nullable(),
  privateIp: z.string(),
  region: z.string(),
  useWowzaInput: z.boolean(),
  inputType: z.string()
})

export const Tournament = z.object({
  id: z.number(),
  name: z.string(),
  property: Property,
  useTranscoderRedundancy: z.boolean(),
  dvr: z.number(),
  drm: z.boolean().nullable(),
  radio: z.string(),
  zixiInputType: z.string(),
  normalizeInputStream: z.string(),
  useSSAI: z.string(),
  preferedZixiServer: ZixiServer.nullable(),
  preferedBackupZixiServer: ZixiServer.nullable(),
  posterLocation: z.string().nullable(),
  watermark: z.null(),
  directConnect: z.string(),
  awsProvisioning: z.string(),
  lowLatency: z.string(),
  lowLatencyHost: z.number().nullable(),
  defaultRegion: z.string().nullable(),
  autoProvisioning: z.string(),
  autoProvisioningOffsetMins: z.number().nullable(),
  llHls: z.string(),
  commentary: z.string().optional()
})

export const StreamingConfig = z.object({
  id: z.number(),
  ratio: z.number(),
  lowLatencyPlayerUrl: z.string().nullable(),
  zixiServer: ZixiServer.nullable(),
  backupZixiServer: ZixiServer.nullable(),
  transcoderType: z.string(),
  isLive: z.boolean()
})

export const EncodingEngineer = z.object({
  encodingEngineer: z.object({ email: z.string() })
})

export const GfxConfig = z.object({
  externalEventId: z.string()
})

export const GetEventParser = z.object({
  id: z.number(),
  title: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  tournament: Tournament,
  posterLocation: z.string().nullable(),
  audioLanguages: z.null(),
  court: z.string().nullable(),
  commentary: z.string().nullable(),
  externalId: z.string().nullable(),
  externalProviderId: z.string().nullable(),
  type: z.string(),
  description: z.string().nullable(),
  location: z.string().nullable(),
  directConnect: z.string(),
  awsProvisioning: z.string(),
  lowLatency: z.string(),
  lowLatencyHost: z.null(),
  defaultRegion: z.string().nullable(),
  autoProvisioning: z.string(),
  autoProvisioningOffsetMins: z.null(),
  llHls: z.string(),
  preferedZixiServer: ZixiServer.nullable(),
  preferedBackupZixiServer: ZixiServer.nullable(),
  streamingConfig: StreamingConfig.nullable(),
  streamingConfigs: z.array(StreamingConfig),
  encodingDetails: EncodingEngineer.nullable(),
  details: z.null(),
  scoreType: z.string().nullable(),
  gfxConfig: GfxConfig.nullable(),
  booked: z.null(),
  vodAvailable: z.boolean(),
  dvr: z.number(),
  contacts: z.string(),
  audioOnly: z.string(),
  normalizeInputStream: z.string(),
  useSSAI: z.string(),
  radio: z.string(),
  audioTracks: z.object({ type: z.string(), tracks: z.array(z.unknown()) }),
  epgId: z.null(),
  preferredPublishingPointRegion: z.null(),
  brandColour: z.null(),
  linearChannelLogo: z.string().nullable(),
  linearChannelSecondaryLogo: z.string().nullable(),
  watermark: z.null(),
  trailerId: z.null(),
  captionType: z.null(),
  captionLanguages: z.string().nullable(),
  geoListField: z.string(),
  epgProvider: z.null(),
  epgProviderId: z.null(),
  storeVods: z.boolean(),
  useDRM: z.boolean().nullable(),
  subEvents: z.array(z.unknown()),
  localBroadcasters: z.array(z.unknown()),
  transcoderRedundancy: z.string(),
  epg: z.null(),
  ssai: z.array(z.unknown()),
  version: z.number(),
  tournamentEmbedRestrictions: z.string(),
  eventEmbedRestrictions: z.string(),
  estimatedProvisionTime: z.string().nullable(),
  retryCount: z.null(),
  retryExhausted: z.null(),
  matchType: z.null(),
  is4k: z.boolean(),
  isWhiteList: z.boolean(),
  isDeleted: z.boolean(),
  isCombiner: z.boolean()
})

export type StreamEvent = z.infer<typeof GetEventParser>
